import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { connect, ConnectedProps } from 'react-redux';
import {
  TextField,
  Button,
  Grid,
  Typography,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useIntl } from 'react-intl';
import { Visibility, VisibilityOff } from '@mui/icons-material';

import { ButtonWithLoader } from '../../components/other/Buttons';

import { actions as authActions } from '../../store/ducks/auth.duck';
import { IAppState } from '../../store/rootDuck';
import { useGetMe } from '../home/users/users/hooks/useGetMe';
import { IUser } from './interfaces';

const NewPassword: React.FC<TPropsFromRedux> = ({
  clearNewPassword,
  newPasswordRequest,
  newPasswordLoading,
  newPasswordSuccess,
  newPasswordErrors,
}) => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const { fetchMe } = useGetMe();
  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword(show => !show);
  const [showPasswordRepeat, setShowPasswordRepeat] = useState(false);
  const handleClickShowPasswordRepeat = () => setShowPasswordRepeat(show => !show);

  const onSuccess = async () => {
    await fetchMe().then((user: IUser) => {
      if (
        user.roles?.includes('ROLE_BUYER_STAFF') ||
        user.roles?.includes('ROLE_VENDOR_STAFF')
      ) {
        navigate('/settings/profile');
      } else {
        navigate('/companies/list');
      }
    });
  };

  useEffect(() => {
    if (newPasswordSuccess || newPasswordErrors) {
      enqueueSnackbar(
        newPasswordSuccess
          ? intl.formatMessage({ id: 'AUTH.PASSWORD.SUCCESS' })
          : `${intl.formatMessage({ id: 'ERROR' })} ${newPasswordErrors}`,
        { variant: newPasswordSuccess ? 'success' : 'error' }
      );
    }
    if (newPasswordSuccess) {
      onSuccess();
    }
    return () => {
      clearNewPassword();
    };
  }, [
    newPasswordSuccess,
    newPasswordErrors,
    enqueueSnackbar,
    clearNewPassword,
    navigate,
    intl,
  ]);
  const passwordRequired = [
    { text: intl.formatMessage({ id: 'REGEX.EIGHT.SIMBOL' }), regex: /.{8,}/ },
    { text: intl.formatMessage({ id: 'REGEX.UPPER_CASE' }), regex: /(?=.*[A-Z])/ },
    { text: intl.formatMessage({ id: 'REGEX.ONE.NUMBER' }), regex: /(?=.*\d)/ },
    { text: intl.formatMessage({ id: 'REGEX.ONE.SPECIAL.SIMBOL' }), regex: /(?=.*[\W_])/ },
  ];
  return (
    <div className='kt-login__body'>
      <div className='kt-login__form'>
        <div className='kt-login__title'>
          <h3>{intl.formatMessage({ id: 'AUTH.PASSWORD.NEW_TITLE' })}</h3>
        </div>

        <Formik
          initialValues={{ password: '', password2: '' }}
          validationSchema={Yup.object().shape({
            password: Yup.string().required(intl.formatMessage({ id: 'AUTH.PASSWORD.INPUT' })),
            password2: Yup.string()
              .required(intl.formatMessage({ id: 'AUTH.PASSWORD.REPEAT' }))
              .oneOf(
                [Yup.ref('password'), ''],
                intl.formatMessage({ id: 'AUTH.PASSWORD.EQUAL' })
              ),
          })}
          onSubmit={values => {
            if (/^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/.test(values.password)) {
              newPasswordRequest({
                password: values.password,
                password2: values.password2,
                code: code || '',
              });
            }
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
            <form onSubmit={handleSubmit} className='kt-form' style={{ textAlign: 'center' }}>
              <div role='alert' className='alert '>
                <div className='alert-text'>
                  {intl.formatMessage({ id: 'AUTH.PASSWORD.INPUT_TITLE' })}
                </div>
              </div>
              <div style={{ textAlign: 'left' }}>
                <Typography>{intl.formatMessage({ id: 'AUTH.PASSWORD.RULES' })}</Typography>
                {passwordRequired.map(({ text, regex }) => (
                  <li
                    style={{
                      color:
                        touched.password && !regex.test(values.password) ? '#bf1f2c' : '#000',
                    }}
                    key={text}
                  >
                    {text}
                  </li>
                ))}
              </div>
              <div className='form-group'>
                <TextField
                  autoComplete='off'
                  type={showPassword ? 'text' : 'password'}
                  margin='normal'
                  label={intl.formatMessage({ id: 'AUTH.FORM.PASSWORD' })}
                  className='kt-width-full'
                  name='password'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.password}
                  helperText={touched.password && errors.password}
                  error={Boolean(
                    touched.password &&
                      (errors.password ||
                        !/^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/.test(values.password))
                  )}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          aria-label='toggle password visibility'
                          onClick={handleClickShowPassword}
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>

              <div className='form-group'>
                <TextField
                  autoComplete='off'
                  type={showPasswordRepeat ? 'text' : 'password'}
                  margin='normal'
                  label={intl.formatMessage({ id: 'AUTH.FORM.PASSWORD_REPEAT' })}
                  className='kt-width-full'
                  name='password2'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.password2}
                  helperText={touched.password2 && errors.password2}
                  error={Boolean(touched.password2 && errors.password2)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          aria-label='toggle password visibility'
                          onClick={handleClickShowPasswordRepeat}
                        >
                          {showPasswordRepeat ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>

              <div className='kt-login__actions'>
                <Grid
                  container
                  direction='row'
                  justifyContent='space-between'
                  alignItems='stretch'
                >
                  <Button onClick={() => navigate('/auth')} variant='text' color='success'>
                    {intl.formatMessage({ id: 'COMMON.BUTTON.BACK' })}
                  </Button>

                  <ButtonWithLoader disabled={newPasswordLoading} loading={newPasswordLoading}>
                    {intl.formatMessage({ id: 'AUTH.PASSWORD.BUTTON' })}
                  </ButtonWithLoader>
                </Grid>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
};

const connector = connect(
  (state: IAppState) => ({
    newPasswordLoading: state.auth.newPasswordLoading,
    newPasswordSuccess: state.auth.newPasswordSuccess,
    newPasswordErrors: state.auth.newPasswordErrors,
  }),
  {
    clearNewPassword: authActions.clearNewPassword,
    newPasswordRequest: authActions.newPasswordRequest,
  }
);

type TPropsFromRedux = ConnectedProps<typeof connector>;

export default connector(NewPassword);
